import React, { useState, useEffect } from 'react';
import { AutoComplete, Button, Input, Modal, Table, Tag, Tooltip, Typography } from 'antd';
import DeloitteLoader from '../../../../ui/DeloitteLoader';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import SearchIcon from '../../../../../assets/icons/search-icon.svg';
import CloseCircle from '../../../../../assets/icons/close-circle.svg';
import { GenAIActions } from '../../../../../libs/store/actions/GenAIActions';
import {
  IAutoCompleteSearchPayload,
  ICreateSessionPayload,
  ITierEstimate,
  RoutingMode,
} from '../../../../../libs/models/GenAIModels';
import { IApplicationState, useAppSelector } from '../../../../../libs/store/reducers';
import { STORAGE_CONSTANTS, storage } from '../../../../../libs/services/LocalStorage';

const DEBOUNCE_DELAY = 1000;
interface ISearchBoxProps {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  isDrawer: boolean;
  isKeywordSearch: boolean;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedSearchHistoryId?: string;
  setCardContent: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsGlobalSearch: React.Dispatch<React.SetStateAction<boolean>>;
  model?: string;
  routingMode?: RoutingMode;
  disableCache?: boolean;
}

const SearchBox: React.FC<ISearchBoxProps> = ({
  setShowModal,
  isDrawer,
  isKeywordSearch,
  query,
  setQuery,
  setVisible,
  setCardContent,
  setIsGlobalSearch,
  selectedSearchHistoryId,
  model,
  routingMode,
  disableCache,
}) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isEstimateModalOpen, setIsEstimateModalOpen] = useState<boolean>(false);
  const [estimatedFor, setEstimatedFor] = useState<string>('');
  const handleInputChange = (value: string) => {
    setQuery(value);
  };
  const { autoCompleteOptions, loading: loadingAutosuggestions, queryEstimate, estimating } =
    useAppSelector(
      ({ genAI: { autoCompleteOptions, loading, queryEstimate, estimating } }: IApplicationState) => ({
        autoCompleteOptions,
        loading,
        queryEstimate,
        estimating,
      }),
    );
  const userId = storage.getItem(STORAGE_CONSTANTS.userId)!;

  const dispatch = useDispatch();
  const dispatcher = {
    fetchSuggestions: (data: IAutoCompleteSearchPayload) =>
      dispatch(GenAIActions.getAutoCompleteOptionsStart(data)),
    createOrModifySession: (payload: ICreateSessionPayload) =>
      dispatch(GenAIActions.currentChatSessionStart(payload)),
    estimateQuery: (queryText: string) =>
      dispatch(
        GenAIActions.estimateQueryStart({
          query: queryText,
          disableCache,
          model,
          routingMode,
        }),
      ),
    clearEstimate: () => dispatch(GenAIActions.clearQueryEstimate()),
  };

  const dispatchSend = (queryText: string) => {
    dispatcher.createOrModifySession({
      userId,
      query: queryText,
      sessionId: selectedSearchHistoryId,
      isDocSearch: !isKeywordSearch,
      model,
      routingMode,
      disableCache,
    });
    setCardContent(queryText);
  };

  const handleGenerateClick = () => {
    if (isDrawer) {
      dispatchSend(query);
    } else {
      setVisible(true);
      setShowModal(false);
      setIsGlobalSearch(true);
      setCardContent(query);
    }
    setSuggestions([]);
    setQuery('');
    setIsEstimateModalOpen(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
  };

  const handleClearQuery = () => {
    setQuery('');
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (!open) {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    autoCompleteOptions && setSuggestions(autoCompleteOptions);
  }, [autoCompleteOptions]);

  // useEffect(() => {
  //   if (query) {
  //     const suggestionsTimeoutId = setTimeout(() => {
  //       dispatcher.fetchSuggestions({
  //         searchText: query,
  //       });
  //     }, DEBOUNCE_DELAY);
  //     return () => clearTimeout(suggestionsTimeoutId);
  //   }
  // }, [query]);

  // Fetches (once per distinct query) the tier/cost estimate shown by the $ button.
  const ensureEstimate = () => {
    if (!query || estimatedFor === query) return;
    setEstimatedFor(query);
    dispatcher.clearEstimate();
    dispatcher.estimateQuery(query);
  };

  const handleEstimateClick = () => {
    ensureEstimate();
    setIsEstimateModalOpen(true);
  };

  const handleCloseEstimate = () => {
    setIsEstimateModalOpen(false);
  };

  const recommended = queryEstimate?.tiers.find((t) => t.tier === queryEstimate.recommendedTier);
  const tooltipTitle = !query ? (
    'Type a query to see its estimated cost'
  ) : estimating && estimatedFor === query ? (
    'Estimating cost…'
  ) : recommended ? (
    <>
      <div>{`Recommended (${recommended.tier.replace('_', ' ')}): $${recommended.estimatedCost.toFixed(6)}`}</div>
      <div>{'Click for full breakdown'}</div>
    </>
  ) : (
    'Click to estimate cost'
  );

  const tierColumns = [
    { title: 'Tier', dataIndex: 'tier', key: 'tier', render: (t: string) => t.replace('_', ' ') },
    { title: 'Model', dataIndex: 'model', key: 'model' },
    { title: 'Est. Input Tokens', dataIndex: 'estimatedInputTokens', key: 'estimatedInputTokens' },
    { title: 'Est. Output Tokens', dataIndex: 'estimatedOutputTokens', key: 'estimatedOutputTokens' },
    {
      title: 'Est. Cost',
      dataIndex: 'estimatedCost',
      key: 'estimatedCost',
      render: (cost: number) => `$${cost.toFixed(6)}`,
    },
    {
      title: 'Basis',
      dataIndex: 'estimateBasis',
      key: 'estimateBasis',
      render: (basis: ITierEstimate['estimateBasis'], row: ITierEstimate) =>
        basis === 'learned' ? (
          <Tag color="gold" title={`Averaged from ${row.sampleSize} past runs on this tier`}>
            {`learned (${row.sampleSize})`}
          </Tag>
        ) : (
          <Tag title="Not enough past runs yet — using a default output/input ratio">default</Tag>
        ),
    },
  ];

  return (
    <div className="modal-container-search-box">
      <div className="w-100 d-flex">
        {isDrawer && (
          <div className="search-icon-container">
            <img src={SearchIcon} className="search-icon" alt="search" />
          </div>
        )}
        <div className="search">
          <AutoComplete
            value={query}
            options={
              loadingAutosuggestions
                ? []
                : suggestions.map((suggestion) => ({
                    value: suggestion,
                    label: suggestion,
                    key: suggestion,
                  }))
            }
            notFoundContent={
              loadingAutosuggestions ? (
                <div className="flex-center w-100">
                  <DeloitteLoader size={32} style={{ display: 'block', margin: 'auto' }} />
                </div>
              ) : undefined
            }
            dropdownClassName={
              isDrawer ? 'genai-drawer-dropdown-wrapper' : 'genai-modal-dropdown-wrapper'
            }
            onSelect={handleSuggestionClick}
            onChange={handleInputChange}
            dropdownMatchSelectWidth={false}
            onDropdownVisibleChange={handleDropdownVisibleChange}
          >
            <div className="text-side">
              <Input.TextArea
                placeholder={'Start typing...'}
                value={query}
                className={isDrawer ? 'italic search-box-textarea' : 'normal search-box-textarea'}
                autoSize={{ minRows: 1, maxRows: 6 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleGenerateClick();
                  }
                }}
              />
            </div>
          </AutoComplete>
        </div>
      </div>
      <div className="close-circle-and-generate-button">
        {!isDrawer && (
          <img
            src={CloseCircle}
            className="close-circle-outlined"
            onClick={handleClearQuery}
            alt="Close"
          />
        )}
        <span className="estimate-cost-button-wrap">
          <Tooltip title={tooltipTitle} onVisibleChange={(visible) => visible && ensureEstimate()}>
            <Button
              shape="circle"
              onClick={handleEstimateClick}
              disabled={isEmpty(query)}
              aria-label="Estimate cost"
            >
              {'$'}
            </Button>
          </Tooltip>
        </span>
        {isDrawer ? (
          <button
            type="button"
            className="chat-panel__send-button"
            onClick={handleGenerateClick}
            disabled={isEmpty(isKeywordSearch ? query : selectedSearchHistoryId)}
            aria-label="Generate"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        ) : (
          <Button
            className="generate-button"
            onClick={handleGenerateClick}
            disabled={isEmpty(isKeywordSearch ? query : selectedSearchHistoryId)}
          >
            <Typography className="generate-button-text">{'Generate'}</Typography>
          </Button>
        )}
        <div>
          {!isDrawer && (
            <Button className="close-button" onClick={() => setShowModal(false)}>
              Close
            </Button>
          )}
        </div>
      </div>
      <Modal
        title="Estimated cost for this query"
        visible={isEstimateModalOpen}
        onCancel={handleCloseEstimate}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseEstimate}>
            Close
          </Button>,
        ]}
        className="estimate-cost-modal"
        destroyOnClose
      >
        {estimating ? (
          <div className="estimate-cost-modal__loading">
            <DeloitteLoader size={32} style={{ display: 'block', margin: 'auto' }} />
          </div>
        ) : (
          <>
            {queryEstimate?.cacheHit && (
              <div className="estimate-cost-modal__banner estimate-cost-modal__banner--cache">
                {`This exact query was answered before by ${queryEstimate.cachedModel} — it will be served from cache at no cost.`}
              </div>
            )}
            {!routingMode || routingMode === 'auto' ? (
              <div className="estimate-cost-modal__banner estimate-cost-modal__banner--auto">
                {`Auto mode will route to: ${queryEstimate?.recommendedTier?.replace('_', ' ')}`}
              </div>
            ) : (
              <div className="estimate-cost-modal__banner estimate-cost-modal__banner--manual">
                {`Manual model selected: ${model}`}
              </div>
            )}
            <Table
              size="small"
              pagination={false}
              rowKey="tier"
              columns={tierColumns}
              dataSource={(queryEstimate?.tiers as ITierEstimate[]) || []}
              rowClassName={(row: ITierEstimate) =>
                row.tier === queryEstimate?.recommendedTier ? 'estimate-cost-modal__row--recommended' : ''
              }
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default SearchBox;
